import React, { useEffect, useState } from "react";
import { SiGithub } from "react-icons/si";
import { FiArrowUpRight } from "react-icons/fi";

const USERNAME = "Norielqt";
const OLDEST_YEAR = 2025;

// GitHub-style contribution colors (light / dark tuned)
const LEVEL_COLORS = {
  light: ["#ebedf0", "#9be9a8", "#40c463", "#30a14e", "#216e39"],
  dark: ["#161b22", "#0e4429", "#006d32", "#26a641", "#39d353"],
};

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Newest → oldest, floored at OLDEST_YEAR
function buildYearList() {
  const current = new Date().getFullYear();
  const years = [];
  for (let y = Math.max(current, OLDEST_YEAR); y >= OLDEST_YEAR; y--) years.push(y);
  return years;
}

// Group the flat day array into columns of weeks (rows = Sun..Sat)
function buildWeeks(contributions) {
  const weeks = [];
  let week = [];
  contributions.forEach((day, i) => {
    const weekday = new Date(day.date).getDay(); // 0 = Sun
    if (i === 0 && weekday !== 0) {
      for (let p = 0; p < weekday; p++) week.push(null);
    }
    week.push(day);
    if (weekday === 6) {
      weeks.push(week);
      week = [];
    }
  });
  if (week.length) weeks.push(week);
  return weeks;
}

const GitHubStats = () => {
  const years = buildYearList();
  const [contrib, setContrib] = useState(null);
  const [year, setYear] = useState(years[0]);
  const [error, setError] = useState(false);

  // Contributions — refetched whenever the year changes
  useEffect(() => {
    let alive = true;
    setContrib(null);
    fetch(`https://github-contributions-api.jogruber.de/v4/${USERNAME}?y=${year}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error("contrib"))))
      .then((data) => alive && setContrib(data))
      .catch(() => alive && setError(true));
    return () => {
      alive = false;
    };
  }, [year]);

  // Don't render the section at all if the API is down / rate-limited
  if (error && !contrib) return null;

  const contribLoading = !contrib;
  const totalContributions =
    contrib?.total != null ? contrib.total[year] ?? Object.values(contrib.total)[0] : null;
  const weeks = contrib?.contributions ? buildWeeks(contrib.contributions) : [];

  return (
    <div id="github" className="mt-20 scroll-mt-24">
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8 md:mb-10">
        <p style={{ fontSize: "14px", color: "#11111199", textTransform: "uppercase", letterSpacing: "0.08em" }} className="font-medium">
          GitHub Activity
        </p>
        <a
          href={`https://github.com/${USERNAME}`}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1.5 text-xs font-medium transition-colors"
          style={{ color: "#11111199" }}
        >
          View GitHub <FiArrowUpRight size={13} />
        </a>
      </div>

      {/* Contribution calendar */}
      <div
        className="rounded-sm shadow-sm p-4 md:p-5 dark:bg-brand-900"
        style={{ backgroundColor: "#F0F0F0" }}
      >
        <div className="flex items-center justify-between gap-4 mb-4">
          <span className="text-xs md:text-sm font-medium text-brand-500">
            {contribLoading ? "Loading…" : `${totalContributions?.toLocaleString?.() ?? 0} contributions in ${year}`}
          </span>
          <label className="flex items-center gap-2 text-xs md:text-sm text-brand-500">
            <span className="hidden sm:inline">Year</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="rounded-md border border-brand-300 bg-white dark:bg-brand-800 dark:border-brand-700 dark:text-white px-2 py-1 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-brand-400 cursor-pointer"
              style={{ color: "#1C1C1E" }}
            >
              {years.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
        </div>

        <div className="overflow-x-auto">
          {contribLoading ? (
            <div className="h-[120px] flex items-center justify-center text-sm text-brand-500">
              Loading contributions…
            </div>
          ) : (
            <Calendar weeks={weeks} />
          )}
        </div>
      </div>
    </div>
  );
};

const Calendar = ({ weeks }) => {
  // Month labels: show a label above the first week where the month changes
  const monthPositions = [];
  let lastMonth = -1;
  weeks.forEach((week, wi) => {
    const firstDay = week.find(Boolean);
    if (!firstDay) return;
    const month = new Date(firstDay.date).getMonth();
    if (month !== lastMonth) {
      monthPositions.push({ index: wi, label: MONTH_LABELS[month] });
      lastMonth = month;
    }
  });

  const isDark =
    typeof document !== "undefined" && document.documentElement.classList.contains("dark");
  const palette = isDark ? LEVEL_COLORS.dark : LEVEL_COLORS.light;

  return (
    <div className="inline-flex flex-col gap-1 min-w-max">
      {/* Month labels */}
      <div className="flex gap-[3px] pl-[26px] mb-1">
        {weeks.map((_, wi) => {
          const label = monthPositions.find((m) => m.index === wi)?.label;
          return (
            <div key={wi} className="w-[11px] text-[10px] text-brand-500 dark:text-brand-400">
              {label || ""}
            </div>
          );
        })}
      </div>

      <div className="flex gap-[3px]">
        {/* Weekday labels */}
        <div className="flex flex-col gap-[3px] pr-1 text-[9px] text-brand-500 dark:text-brand-400">
          {["", "Mon", "", "Wed", "", "Fri", ""].map((d, i) => (
            <div key={i} className="h-[11px] leading-[11px]">{d}</div>
          ))}
        </div>

        {/* Weeks */}
        {weeks.map((week, wi) => (
          <div key={wi} className="flex flex-col gap-[3px]">
            {Array.from({ length: 7 }).map((_, di) => {
              const day = week[di];
              if (!day) return <div key={di} className="w-[11px] h-[11px]" />;
              return (
                <div
                  key={di}
                  title={`${day.count} contribution${day.count === 1 ? "" : "s"} on ${day.date}`}
                  className="w-[11px] h-[11px] rounded-[2px]"
                  style={{ backgroundColor: palette[day.level] || palette[0] }}
                />
              );
            })}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-1 justify-end mt-2 text-[10px] text-brand-500 dark:text-brand-400">
        <span className="mr-1">Less</span>
        {palette.map((c, i) => (
          <div key={i} className="w-[11px] h-[11px] rounded-[2px]" style={{ backgroundColor: c }} />
        ))}
        <span className="ml-1">More</span>
      </div>
    </div>
  );
};

export default GitHubStats;
